import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Download,
  FileSpreadsheet,
  Sparkles,
  CircleHelp,
  X,
  Calendar,
  GraduationCap,
  BookOpen,
  Award,
  Hash,
  FileImage,
  Loader2,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

import CertificateTopDashboard from "./components/CertificateTopDashboard";
import CertificateFilter from "./components/CertificateFilter";
import IssueCertificateModal from "./components/IssueCertificateModal";
import CertificateRequestModal from "./components/CertificateRequestModal";
import TeacherCertificateRequestModal from "./components/TeacherCertificateRequestModal";
import CertificateRequestTypePickerModal from "./components/CertificateRequestTypePickerModal";
import CertificateRequestQueue from "./components/CertificateRequestQueue";
import CertificateZipHistoryTab from "./components/CertificateZipHistoryTab";
import TemplateManageModal from "./components/TemplateManageModal";
import CertificateTemplate from "./components/CertificateTemplate";
import Pagination from "../../../components/common/Pagination";
import {
  getCertificates,
  getCertificateById,
  exportCertificates,
  deleteCertificate,
} from "../../../api/elearning/certificates/certificates";
import CertificateRequestDeleteModal from "./components/CertificateRequestDeleteModal";
import CertificateDownloadQueuePanel from "./components/CertificateDownloadQueuePanel";
import { useCertificateDownloadQueue } from "./hooks/useCertificateDownloadQueue";
import {
  CERT_EXPORT_CHUNK_SIZE,
  chunkArray,
  parseContentDispositionFileName,
  parseExportErrorMessage,
  triggerBlobDownload,
} from "./certificateDownloadUtils";
import { listCertificateRequests } from "../../../api/elearning/certificates/certificateRequests";
import { hasPermission } from "../../../utils/permissionUtils";
import { startCertificatesPageTour } from "./certificatesPageTour";
import { formatIssueDateForCert, buildCertificateDisplayData } from "./certificateLayoutUtils";

const ZIP_HISTORY_ADMIN_EMAIL = "administrator@hamesoft.com";

const sanitizeFilePart = (value, fallback = "ChungNhan") => {
  const normalized = String(value || fallback || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || fallback;
};

const getGradeBadgeClass = (grade) => {
  const raw = String(grade || "").trim().toLowerCase();
  if (raw.includes("xuất sắc") || raw.includes("xuat sac")) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (raw.includes("giỏi") || raw.includes("gioi")) {
    return "bg-red-50 text-red-700 border-red-200";
  }
  if (raw && raw !== "—") {
    return "bg-blue-50 text-blue-700 border-blue-200";
  }
  return "bg-gray-50 text-gray-600 border-gray-200";
};

const CertificateManager = () => {
  const [mainTab, setMainTab] = useState(() => {
    if (hasPermission("elearning.certificates.request", "R")) return "requests";
    return "issued";
  });
  const [showRequest, setShowRequest] = useState(false);
  const [showTeacherRequest, setShowTeacherRequest] = useState(false);
  const [pickRequestTypeOpen, setPickRequestTypeOpen] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [requestQueueRefreshKey, setRequestQueueRefreshKey] = useState(0);
  const [selectedCert, setSelectedCert] = useState(null);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  /** Chỉ bung phần bản xem khi user bấm "Xem giấy chứng nhận" */
  const [certArtifactRequested, setCertArtifactRequested] = useState(false);
  const certArtifactSectionRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const downloadQueue = useCertificateDownloadQueue();
  const [selectingAllFiltered, setSelectingAllFiltered] = useState(false);
  const [deleteCertOpen, setDeleteCertOpen] = useState(false);
  const [deletingCert, setDeletingCert] = useState(false);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [topStats, setTopStats] = useState({
    pendingHq: 0,
    pendingMgmt: 0,
    issuedTotal: 0,
    issuedStudents: 0,
    issuedTeachers: 0,
  });

  const [filters, setFilters] = useState({
    recipient_type: "all",
    cert_type: "all",
    subject: "all",
    grade: "",
    search: "",
    year: new Date().getFullYear().toString(),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const canViewIssued = hasPermission("elearning.certificates", "R");
  const canViewRequests = hasPermission("elearning.certificates.request", "R");
  const canDeleteCertificate = hasPermission("elearning.certificates", "D");
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const canViewZipHistory =
    canViewIssued &&
    String(currentUser?.email || "").trim().toLowerCase() === ZIP_HISTORY_ADMIN_EMAIL;
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canViewRequests) tabs.push({ id: "requests", label: "Yêu cầu cấp chứng nhận" });
    if (canViewIssued) tabs.push({ id: "issued", label: "Chứng nhận đã cấp" });
    if (canViewZipHistory) tabs.push({ id: "zip_history", label: "Nhật ký phê duyệt / tải file" });
    return tabs;
  }, [canViewRequests, canViewIssued, canViewZipHistory]);
  const dashboardVisibleKeys = useMemo(() => {
    const keys = [];
    if (canViewRequests) keys.push("pendingHq", "pendingMgmt");
    if (canViewIssued) keys.push("issuedTotal", "issuedStudents", "issuedTeachers");
    return keys;
  }, [canViewIssued, canViewRequests]);
  const canQuickTour = canViewIssued;
  const canManageCertTemplates = hasPermission("elearning.certificates.template", "C");
  const canCreateCertificateRequest = hasPermission("elearning.certificates.request", "C");
  const canCreateStudentReq = hasPermission("elearning.certificates.student", "C");
  const canCreateTeacherReq = hasPermission("elearning.certificates.teacher", "C");
  const canShowCreateRequestBtn = canCreateStudentReq || canCreateTeacherReq;
  const canDirectIssue = canManageCertTemplates && canCreateCertificateRequest;
  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        size: pageSize,
        keyword: filters.search || undefined,
      };
      if (filters.recipient_type !== "all") params.recipient_type = filters.recipient_type;
      if (filters.cert_type !== "all") params.cert_type = filters.cert_type;
      if (filters.subject && filters.subject !== "all") params.subject_id = filters.subject;
      if (String(filters.grade || "").trim()) params.grade = String(filters.grade).trim();
      const data = await getCertificates(params);
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch {
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    if (!canViewIssued) {
      setLoading(false);
      setRecords([]);
      setTotal(0);
      return;
    }
    fetchCertificates();
  }, [canViewIssued, fetchCertificates]);

  const loadTopStats = useCallback(async () => {
    setStatsLoading(true);
    const empty = {
      pendingHq: 0,
      pendingMgmt: 0,
      issuedTotal: 0,
      issuedStudents: 0,
      issuedTeachers: 0,
    };
    try {
      const canIssued = hasPermission("elearning.certificates", "R");
      const canReq = hasPermission("elearning.certificates.request", "R");
      const tasks = [];
      if (canReq) {
        tasks.push(
          listCertificateRequests({ page: 1, size: 1, status: "pending_hq" }).then((d) => ["hq", d]),
          listCertificateRequests({ page: 1, size: 1, status: "pending_mgmt" }).then((d) => ["mgmt", d])
        );
      }
      if (canIssued) {
        tasks.push(
          getCertificates({ page: 1, size: 1 }).then((d) => ["total", d]),
          getCertificates({ page: 1, size: 1, recipient_type: "student" }).then((d) => ["stu", d]),
          getCertificates({ page: 1, size: 1, recipient_type: "teacher" }).then((d) => ["tea", d])
        );
      }
      if (tasks.length === 0) {
        setTopStats(empty);
      } else {
        const results = await Promise.all(tasks);
        const next = { ...empty };
        for (const [key, d] of results) {
          const t = Number(d?.total || 0);
          if (key === "hq") next.pendingHq = t;
          if (key === "mgmt") next.pendingMgmt = t;
          if (key === "total") next.issuedTotal = t;
          if (key === "stu") next.issuedStudents = t;
          if (key === "tea") next.issuedTeachers = t;
        }
        setTopStats(next);
      }
    } catch {
      setTopStats(empty);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTopStats();
  }, [loadTopStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.recipient_type, filters.cert_type, filters.subject, filters.grade, filters.search]);

  useEffect(() => {
    if (availableTabs.some((tab) => tab.id === mainTab)) return;
    if (availableTabs.length > 0) {
      setMainTab(availableTabs[0].id);
    }
  }, [mainTab, availableTabs]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, pageSize, filters.recipient_type, filters.cert_type, filters.subject, filters.grade, filters.search]);

  useEffect(() => {
    setCertArtifactRequested(false);
    setPreviewDetail(null);
    setPreviewLoading(false);
  }, [selectedCert?.id]);

  useEffect(() => {
    if (!selectedCert?.id) return;
    let cancelled = false;
    setPreviewLoading(true);
    getCertificateById(selectedCert.id)
      .then((d) => {
        if (!cancelled) setPreviewDetail(d);
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewDetail(null);
          toast.error("Không thể tải chi tiết chứng nhận.");
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCert?.id]);

  useEffect(() => {
    if (!certArtifactRequested) return undefined;
    const scrollToArtifact = () => {
      certArtifactSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t1 = window.setTimeout(scrollToArtifact, 50);
    const t2 = window.setTimeout(scrollToArtifact, 350);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [certArtifactRequested, previewLoading, previewDetail]);

  const closeCertDetailModal = () => {
    setDeleteCertOpen(false);
    setSelectedCert(null);
  };

  const handleDeleteIssuedCertificate = async (deleteReason) => {
    if (!selectedCert?.id) return;
    setDeletingCert(true);
    try {
      await deleteCertificate(selectedCert.id, { delete_reason: deleteReason });
      toast.success("Đã xóa chứng nhận.");
      setDeleteCertOpen(false);
      setSelectedIds((prev) => prev.filter((id) => id !== selectedCert.id));
      closeCertDetailModal();
      fetchCertificates();
      loadTopStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể xóa chứng nhận.");
    } finally {
      setDeletingCert(false);
    }
  };

  const certInfoItem = (Icon, label, value, iconColor = "text-gray-400") => (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
      <div className={`p-2 rounded-lg bg-gray-50 ${iconColor}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{label}</p>
        <p className="text-[13px] font-semibold text-gray-700">{value != null && value !== "" ? value : "—"}</p>
      </div>
    </div>
  );

  const detailCert = previewDetail || selectedCert;

  const paginatedData = records;
  const allOnPageSelected = useMemo(() => {
    if (!paginatedData.length) return false;
    return paginatedData.every((r) => selectedIds.includes(r.id));
  }, [paginatedData, selectedIds]);

  const toggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllOnPage = () => {
    if (!paginatedData.length) return;
    const pageIds = paginatedData.map((r) => r.id);
    setSelectedIds((prev) => {
      const hasAll = pageIds.every((id) => prev.includes(id));
      if (hasAll) return prev.filter((id) => !pageIds.includes(id));
      return Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const fetchAllFilteredCertificateIds = async (limitMax = 500) => {
    const baseParams = {
      size: 100,
      keyword: filters.search || undefined,
      recipient_type: filters.recipient_type !== "all" ? filters.recipient_type : undefined,
      cert_type: filters.cert_type !== "all" ? filters.cert_type : undefined,
      subject_id: filters.subject && filters.subject !== "all" ? filters.subject : undefined,
      grade: String(filters.grade || "").trim() || undefined,
    };
    const first = await getCertificates({ ...baseParams, page: 1 });
    const totalRows = Number(first?.total || 0);
    const maxRows = Math.min(totalRows, limitMax);
    let ids = (first?.records || []).map((r) => r.id);
    const totalPages = Math.ceil(maxRows / 100);

    for (let page = 2; page <= totalPages; page += 1) {
      const d = await getCertificates({ ...baseParams, page });
      ids = ids.concat((d?.records || []).map((r) => r.id));
      if (ids.length >= maxRows) break;
    }
    return ids.slice(0, maxRows);
  };

  const enqueueCertificateExport = useCallback(
    ({ ids, format, title, subtitle, fileName }) => {
      const jobId = `cert-export-${format}-${[...ids].sort().join(",")}`;
      if (downloadQueue.isJobActive(jobId)) return jobId;

      return downloadQueue.enqueue({
        id: jobId,
        title,
        subtitle,
        fileName,
        totalItems: ids.length,
        execute: async ({ onProgress }) => {
          try {
            onProgress({ phase: "preparing", progress: null });
            const res = await exportCertificates(ids, format, {
              onDownloadProgress: (event) => {
                const loadedBytes = Number(event?.loaded || 0);
                const totalBytes = Number(event?.total || 0);
                const progress =
                  totalBytes > 0
                    ? Math.max(0, Math.min(100, Math.round((loadedBytes / totalBytes) * 100)))
                    : null;
                onProgress({
                  phase: "downloading",
                  loadedBytes,
                  totalBytes,
                  progress,
                });
              },
            });
            const blob = res.data;
            const ext = format === "pdf" ? "pdf" : format === "png" ? "png" : "zip";
            const headerFileName = parseContentDispositionFileName(
              res?.headers?.["content-disposition"] || ""
            );
            const resolvedName =
              fileName ||
              headerFileName ||
              `ChungNhan_Batch_${new Date().toISOString().slice(0, 10)}.${ext}`;
            onProgress({ phase: "saving", progress: 100, fileName: resolvedName });
            triggerBlobDownload(blob, resolvedName);
          } catch (err) {
            const message = await parseExportErrorMessage(err, "Không thể tải chứng nhận.");
            throw new Error(message);
          }
        },
      });
    },
    [downloadQueue]
  );

  const downloadBatch = (ids, format) => {
    if (!ids.length) {
      toast.warning("Vui lòng chọn chứng nhận cần tải.");
      return;
    }

    const chunks = chunkArray(ids, CERT_EXPORT_CHUNK_SIZE);
    const dateStr = new Date().toISOString().slice(0, 10);
    const formatLabel = format === "pdf" ? "PDF gộp" : format === "png" ? "PNG" : "ZIP ảnh";

    chunks.forEach((chunkIds, index) => {
      const partLabel = chunks.length > 1 ? `Phần ${index + 1}/${chunks.length}` : null;
      enqueueCertificateExport({
        ids: chunkIds,
        format,
        title: `${formatLabel} (${chunkIds.length} giấy)`,
        subtitle: partLabel || `${chunkIds.length} chứng nhận`,
        fileName:
          format === "png"
            ? null
            : chunks.length > 1
            ? `ChungNhan_Batch_${dateStr}_p${index + 1}.${format === "pdf" ? "pdf" : "zip"}`
            : null,
      });
    });

    if (chunks.length > 1) {
      toast.info(
        `Đã chia ${ids.length} chứng nhận thành ${chunks.length} tác vụ tải (tối đa ${CERT_EXPORT_CHUNK_SIZE}/file).`
      );
    }
  };

  const handleDownloadSingleImage = (cert) => {
    if (!cert?.id) return;
    const fallbackName = [
      sanitizeFilePart(cert.recipient_name, "HocVien"),
      sanitizeFilePart(cert.cert_id, "ChungNhan"),
    ].join("_");

    enqueueCertificateExport({
      ids: [cert.id],
      format: "png",
      title: `Ảnh chứng nhận — ${cert.recipient_name || "Học viên"}`,
      subtitle: cert.cert_id || cert.cert_id_display || "",
      fileName: `${fallbackName}.png`,
    });
  };

  const handleSelectAllFiltered = async () => {
    try {
      setSelectingAllFiltered(true);
      const ids = await fetchAllFilteredCertificateIds(500);
      setSelectedIds(ids);
      toast.success(`Đã chọn ${ids.length} chứng nhận theo bộ lọc.`);
    } catch {
      toast.error("Không thể chọn danh sách theo bộ lọc.");
    } finally {
      setSelectingAllFiltered(false);
    }
  };
  const handleQuickTour = () => {
    window.setTimeout(() => startCertificatesPageTour(), 80);
  };
  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-certificates-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-6 md:p-8 bg-[#F8FAFC] min-h-screen">
      <div className="mb-6 flex items-start justify-between gap-3" data-tour="certificates-header">
        <div>
          <h1 className="text-2xl font-black text-gray-800 uppercase tracking-tight">
            Quản lý <span className="text-red-700">Giấy chứng nhận</span>
          </h1>
          <p className="text-gray-500 text-sm mt-1">Hệ thống cấp và theo dõi chứng chỉ IMA</p>
        </div>
        {canQuickTour ? (
          <div className="flex items-center gap-2" data-tour="certificates-header-guides">
            <HeaderIconButton icon={Sparkles} label="Hướng dẫn nhanh" onClick={handleQuickTour} />
            <HeaderIconButton icon={CircleHelp} label="Hướng dẫn sử dụng" onClick={openGuide} />
          </div>
        ) : null}
      </div>

      {canViewRequests && dashboardVisibleKeys.length > 0 ? (
        <CertificateTopDashboard
          loading={statsLoading}
          stats={topStats}
          visibleKeys={dashboardVisibleKeys}
        />
      ) : null}

      {availableTabs.length > 1 ? (
        <div className="flex gap-2 mb-4 border-b border-gray-200 pb-1" data-tour="certificates-tabs">
          {availableTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMainTab(tab.id)}
              className={`px-5 py-2.5 rounded-t-xl text-sm font-black uppercase tracking-wide transition-colors ${
                mainTab === tab.id
                  ? "bg-white text-red-800 border border-b-0 border-gray-200 -mb-px"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      {mainTab === "issued" && canViewIssued && (
        <>
      <div data-tour="certificates-filter">
        <CertificateFilter
          filters={filters}
          setFilters={setFilters}
          onRefresh={fetchCertificates}
          onIssue={() => {
            if (canDirectIssue) setShowIssue(true);
          }}
          showDirectIssueButton={canDirectIssue}
        />
      </div>

      {/* MAIN CONTENT SECTION */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={toggleSelectAllOnPage}
            className="px-3 py-2 rounded-xl text-xs font-black uppercase tracking-wide border border-gray-200 text-gray-700 hover:bg-gray-50"
            disabled={loading || downloadQueue.isBusy || !paginatedData.length}
          >
            {allOnPageSelected ? "Bỏ chọn trang này" : "Chọn trang này"}
          </button>
          <button
            type="button"
            onClick={handleSelectAllFiltered}
            className="px-3 py-2 rounded-xl text-xs font-black uppercase tracking-wide border border-gray-200 text-gray-700 hover:bg-gray-50"
            disabled={loading || selectingAllFiltered || downloadQueue.isBusy}
          >
            Chọn theo bộ lọc (tối đa 500)
          </button>
          <span className="text-xs font-bold text-gray-500 ml-1">
            Đã chọn: <span className="text-red-700">{selectedIds.length}</span>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => downloadBatch(selectedIds, "pdf")}
              disabled={!selectedIds.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-700 text-white text-xs font-black uppercase tracking-wide disabled:opacity-50"
            >
              <Download size={14} /> Tải PDF gộp
            </button>
            <button
              type="button"
              onClick={() => downloadBatch(selectedIds, "zip")}
              disabled={!selectedIds.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-800 text-white text-xs font-black uppercase tracking-wide disabled:opacity-50"
            >
              <Download size={14} /> Tải ZIP ảnh
            </button>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden"
          data-tour="certificates-table"
        >
          <div className="overflow-x-auto relative min-h-[200px]">
  {loading && (
    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-[2rem]">
      <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
    </div>
  )}
  <table className="w-full text-left border-collapse">
    <thead className="bg-gray-50/50 border-b border-gray-100">
      <tr>
        <th className="p-5 text-center w-12">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={toggleSelectAllOnPage}
            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-400"
          />
        </th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest w-16 text-center">STT</th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[250px]">Người nhận</th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[280px]">Thông tin chứng nhận</th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center min-w-[150px]">Kết quả</th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[160px]">Ngày cấp</th>
        <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center w-[90px]">Tác vụ</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-50">
      {(loading ? [] : paginatedData).map((cert, index) => (
        <tr key={cert.id} onClick={() => setSelectedCert(cert)} className="hover:bg-gray-50/50 transition-colors group cursor-pointer">
          <td className="p-5 text-center align-top" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedIds.includes(cert.id)}
              onChange={() => toggleRow(cert.id)}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-400"
            />
          </td>
          <td className="p-5 text-center align-top">
            <span className="text-xs font-semibold text-gray-400">
              {(currentPage - 1) * pageSize + index + 1}
            </span>
          </td>
          <td className="p-5 align-top">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-red-700 flex items-center justify-center font-semibold text-sm shrink-0">
                {(cert.recipient_name || "—").charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">{cert.recipient_name}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                      cert.recipient_type === "teacher"
                        ? "bg-indigo-50 text-indigo-700"
                        : "bg-amber-50 text-amber-800"
                    }`}
                  >
                    {cert.recipient_type === "teacher" ? "Giáo viên" : "Học viên"}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Số hiệu: {cert.cert_id_display || cert.cert_id}
                  </span>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                  Số vào sổ: <span className="font-medium text-gray-700">{cert.register_no_display || cert.register_no || "—"}</span>
                </p>
              </div>
            </div>
          </td>
          <td className="p-5 align-top">
            <p className="text-sm font-semibold text-gray-800">{cert.course_name}</p>
            <p className="mt-1 text-xs text-gray-500">{cert.subject}</p>
            <p className="mt-2 text-[11px] text-gray-400">
              Mẫu: <span className="font-medium text-gray-600">{cert.template_name || "—"}</span>
            </p>
          </td>
          <td className="p-5 text-center align-top">
            <span className={`inline-flex px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase border ${getGradeBadgeClass(cert.grade)}`}>
              {cert.grade}
            </span>
            <p className="mt-2 text-[11px] text-gray-400 uppercase tracking-wide">
              {cert.cert_type ? cert.cert_type.replace(/_/g, " ") : "—"}
            </p>
          </td>
          <td className="p-5 align-top">
            <p className="text-sm font-medium text-gray-700">
              {formatIssueDateForCert(cert.issue_date)}
            </p>
            <p className="mt-2 text-[11px] text-gray-400">
              Trạng thái: <span className="font-medium text-gray-600">{cert.status || "issued"}</span>
            </p>
          </td>
          <td className="p-5 text-center align-top" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => handleDownloadSingleImage(cert)}
              className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100 disabled:opacity-50"
              title="Tải ảnh giấy chứng nhận"
              disabled={downloadQueue.isJobActive(`cert-export-png-${cert.id}`)}
            >
              <Download size={18}/>
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

          {!loading && paginatedData.length === 0 && (
            <div className="p-20 text-center">
              <div className="bg-gray-50 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-gray-200">
                <FileSpreadsheet className="text-gray-300" size={32} />
              </div>
              <p className="text-gray-400 font-black italic uppercase text-xs tracking-widest">Không tìm thấy dữ liệu</p>
            </div>
          )}
        </motion.div>

        {/* PHÂN TRANG - Chỉ hiển thị khi có dữ liệu */}
        {total > 0 && (
          <Pagination
            totalRecords={total}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>
        </>
      )}

      {mainTab === "requests" && canViewRequests && (
        <div data-tour="certificates-requests">
          <CertificateRequestQueue
            onTemplates={() => setShowTemplates(true)}
            showCreateRequestButton={canShowCreateRequestBtn}
            onOpenRequestPicker={() => setPickRequestTypeOpen(true)}
            onChanged={loadTopStats}
            showTemplatesButton={canManageCertTemplates}
            refreshSignal={requestQueueRefreshKey}
          />
        </div>
      )}

      {mainTab === "zip_history" && canViewZipHistory && (
        <div data-tour="certificates-zip-history">
          <CertificateZipHistoryTab />
        </div>
      )}

      <CertificateRequestTypePickerModal
        isOpen={pickRequestTypeOpen}
        onClose={() => setPickRequestTypeOpen(false)}
        canStudent={canCreateStudentReq}
        canTeacher={canCreateTeacherReq}
        onPickStudent={() => {
          setPickRequestTypeOpen(false);
          setShowRequest(true);
        }}
        onPickTeacher={() => {
          setPickRequestTypeOpen(false);
          setShowTeacherRequest(true);
        }}
      />

      <CertificateRequestModal
        isOpen={showRequest}
        onClose={() => setShowRequest(false)}
        onSuccess={() => {
          if (canViewRequests) setMainTab("requests");
          setRequestQueueRefreshKey((v) => v + 1);
          loadTopStats();
        }}
      />
      <TeacherCertificateRequestModal
        isOpen={showTeacherRequest}
        onClose={() => setShowTeacherRequest(false)}
        onSuccess={() => {
          if (canViewRequests) setMainTab("requests");
          setRequestQueueRefreshKey((v) => v + 1);
          loadTopStats();
        }}
      />
      {canDirectIssue ? (
        <IssueCertificateModal
          isOpen={showIssue}
          onClose={() => setShowIssue(false)}
          onSuccess={() => {
            fetchCertificates();
            loadTopStats();
          }}
        />
      ) : null}
      <TemplateManageModal
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSuccess={() => {
          if (canViewIssued) fetchCertificates();
          loadTopStats();
        }}
      />

      <AnimatePresence>
        {selectedCert && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[150] flex items-center justify-center p-4"
              onClick={closeCertDetailModal}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white w-full max-w-2xl lg:max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center text-2xl font-black border border-red-100 shadow-sm shrink-0">
                      {(previewDetail?.student_name || selectedCert.recipient_name || "—").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 text-left">
                      <h2 className="text-xl font-bold text-gray-800 truncate">
                        Chi tiết chứng nhận
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase truncate max-w-[12rem]" title={selectedCert.cert_id}>
                          Số: {selectedCert.cert_id || "—"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border shrink-0 ${
                            selectedCert.recipient_type === "teacher"
                              ? "bg-indigo-50 border-indigo-100 text-indigo-700"
                              : "bg-amber-50 border-amber-100 text-amber-800"
                          }`}
                        >
                          {selectedCert.recipient_type === "teacher" ? "Giáo viên" : "Học viên"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeCertDetailModal}
                    className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all shrink-0"
                    aria-label="Đóng"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="min-h-0 space-y-3 text-left">
                        <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-1 flex items-center gap-2">
                          <span className="w-1 h-4 bg-red-500 rounded-full" /> Đối tượng nhận
                        </h3>
                        <div className="space-y-1">
                          {certInfoItem(
                            Award,
                            selectedCert.recipient_type === "teacher" ? "Giáo viên" : "Học viên",
                            detailCert?.student_name ?? detailCert?.recipient_name,
                            "text-red-600"
                          )}
                          {certInfoItem(
                            Hash,
                            "Biểu mẫu",
                            detailCert?.template_name || (previewLoading ? "Đang tải..." : "—"),
                            "text-gray-500"
                          )}
                          {certInfoItem(
                            Hash,
                            "Loại chứng nhận",
                            detailCert?.cert_type ? String(detailCert.cert_type).replace(/_/g, " ") : "—",
                            "text-gray-500"
                          )}
                          {certInfoItem(
                            Hash,
                            "Năm sinh",
                            detailCert?.birth_year || "—",
                            "text-gray-500"
                          )}
                          {certInfoItem(
                            Hash,
                            "Đơn vị",
                            detailCert?.organization_unit || "—",
                            "text-gray-500"
                          )}
                        </div>
                      </div>
                      <div className="min-h-0 space-y-3 text-left">
                        <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-1 flex items-center gap-2">
                          <span className="w-1 h-4 bg-red-500 rounded-full" /> Nội dung chứng nhận
                        </h3>
                        <div className="space-y-1">
                          {certInfoItem(BookOpen, "Khóa học", detailCert?.course_name, "text-blue-600")}
                          {certInfoItem(GraduationCap, "Môn học", detailCert?.subject, "text-indigo-600")}
                          {certInfoItem(Award, "Kết quả", detailCert?.grade, "text-amber-600")}
                          {certInfoItem(
                            Calendar,
                            "Ngày cấp",
                            formatIssueDateForCert(detailCert?.issue_date),
                            "text-gray-600"
                          )}
                          {certInfoItem(Hash, "Số hiệu", detailCert?.cert_id_display || detailCert?.cert_id, "text-gray-500")}
                          {certInfoItem(Hash, "Số vào sổ", detailCert?.register_no_display || detailCert?.register_no, "text-gray-500")}
                          {certInfoItem(Hash, "Nơi cấp", detailCert?.issue_place, "text-gray-600")}
                          {certInfoItem(
                            Calendar,
                            "Hết hiệu lực",
                            formatIssueDateForCert(detailCert?.expiry_date),
                            "text-gray-600"
                          )}
                        </div>
                      </div>
                    </div>

                    {certArtifactRequested ? (
                      <div ref={certArtifactSectionRef} className="border-t border-gray-100 pt-4 scroll-mt-4">
                        <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                          <span className="w-1 h-4 bg-red-500 rounded-full inline-block" /> Bản xem giấy
                        </h3>
                        {previewLoading ? (
                          <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-2xl border border-gray-100">
                            <Loader2 className="animate-spin text-red-600 mb-3" size={28} />
                            <p className="text-[13px] font-semibold text-gray-600">Đang tải bản xem…</p>
                          </div>
                        ) : previewDetail ? (
                          <div className="rounded-2xl border border-gray-100 overflow-hidden">
                            <CertificateTemplate
                              fitContainer
                              data={buildCertificateDisplayData(
                                {
                                  ...detailCert,
                                  recipient_type:
                                    detailCert?.recipient_type ?? selectedCert.recipient_type,
                                  student_name:
                                    detailCert?.student_name ?? selectedCert.recipient_name,
                                  course_name:
                                    detailCert?.course_name ?? selectedCert.course_name,
                                  subject: detailCert?.subject ?? selectedCert.subject,
                                  grade: detailCert?.grade ?? selectedCert.grade,
                                  issue_date:
                                    detailCert?.issue_date ?? selectedCert.issue_date,
                                  cert_id: detailCert?.cert_id ?? selectedCert.cert_id,
                                  register_no:
                                    detailCert?.register_no ?? selectedCert.register_no,
                                  birth_year: detailCert?.birth_year,
                                  organization_unit: detailCert?.organization_unit,
                                  issue_place: detailCert?.issue_place,
                                  expiry_date: detailCert?.expiry_date,
                                },
                                detailCert?.layout_config
                              )}
                              layoutConfig={detailCert?.layout_config}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                            <p className="text-sm font-semibold text-gray-600">Chưa tải được bản xem giấy chứng nhận.</p>
                            <p className="mt-1 text-xs text-gray-400">Vui lòng đóng modal và thử lại.</p>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="p-6 border-t border-gray-50 bg-gray-50/30 flex flex-wrap items-center justify-end gap-3 shrink-0">
                  {canDeleteCertificate ? (
                    <button
                      type="button"
                      disabled={deletingCert}
                      onClick={() => setDeleteCertOpen(true)}
                      className="px-6 py-3 rounded-xl font-bold text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <Trash2 size={18} />
                      XÓA
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={previewLoading && !previewDetail}
                    onClick={() => setCertArtifactRequested((prev) => !prev)}
                    className="px-8 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-65 disabled:pointer-events-none text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-red-100 transition-all active:scale-[0.98]"
                  >
                    {previewLoading && !previewDetail ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Đang tải…
                      </>
                    ) : (
                      <>
                        <FileImage size={18} /> {certArtifactRequested ? "Ẩn bản xem" : "Xem giấy chứng nhận"}
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <CertificateRequestDeleteModal
        open={deleteCertOpen}
        title="Xóa chứng nhận đã cấp"
        description={
          selectedCert
            ? `Bạn sắp xóa chứng nhận số ${selectedCert.cert_id || "—"} của ${
                detailCert?.student_name || selectedCert.recipient_name || "đối tượng nhận"
              }. Chứng nhận sẽ bị thu hồi và không còn hiển thị ở tab Chứng nhận đã cấp. Lý do xóa được lưu lại trên hệ thống.`
            : undefined
        }
        busy={deletingCert}
        onClose={() => !deletingCert && setDeleteCertOpen(false)}
        onConfirm={handleDeleteIssuedCertificate}
      />

      <CertificateDownloadQueuePanel
        jobs={downloadQueue.jobs}
        onDismiss={downloadQueue.clearFinished}
        title="Hàng chờ tải chứng nhận"
      />

    </div>
  );
};

export default CertificateManager;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
        {label}
      </span>
    </button>
  );
}